import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from '../ui/Button';
import { floorPlanTemplates, templateStyles } from '../../data/floorPlanTemplates';

/**
 * TemplateSelector - Modal for browsing and selecting floor plan templates
 */
const TemplateSelector = ({ onSelect, onClose }) => {
  const [selectedStyle, setSelectedStyle] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [targetSqft, setTargetSqft] = useState('');
  const [showPreview, setShowPreview] = useState(false); // For mobile: toggle between list and preview
  const previewCanvasRef = useRef(null);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter templates by style
  const filteredTemplates = useMemo(() => {
    if (selectedStyle === 'all') return floorPlanTemplates;
    return floorPlanTemplates.filter(t => t.style === selectedStyle);
  }, [selectedStyle]);

  // Calculate scale factor based on target sqft
  const scaleFactor = useMemo(() => {
    if (!selectedTemplate || !targetSqft) return 1;
    const target = parseFloat(targetSqft);
    if (isNaN(target) || target <= 0) return 1;
    // Scale is sqrt because area scales with square of linear dimensions
    return Math.sqrt(target / selectedTemplate.baseSqft);
  }, [selectedTemplate, targetSqft]);

  // Scaled dimensions
  const scaledDimensions = useMemo(() => {
    if (!selectedTemplate) return null;
    return {
      width: Math.round(selectedTemplate.totalWidth * scaleFactor),
      height: Math.round(selectedTemplate.totalHeight * scaleFactor),
      sqft: Math.round(selectedTemplate.baseSqft * scaleFactor * scaleFactor),
    };
  }, [selectedTemplate, scaleFactor]);

  // Draw preview canvas
  useEffect(() => {
    if (!selectedTemplate || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Set canvas size
    const containerWidth = canvas.parentElement.clientWidth - 32;
    const containerHeight = 300;
    canvas.width = containerWidth * dpr;
    canvas.height = containerHeight * dpr;
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.fillStyle = '#1a2030';
    ctx.fillRect(0, 0, containerWidth, containerHeight);

    // Calculate scale to fit
    const padding = 20;
    const availableWidth = containerWidth - padding * 2;
    const availableHeight = containerHeight - padding * 2;
    const templateWidth = selectedTemplate.totalWidth * scaleFactor;
    const templateHeight = selectedTemplate.totalHeight * scaleFactor;
    const previewScale = Math.min(
      availableWidth / templateWidth,
      availableHeight / templateHeight
    ) * 0.9;

    // Center offset
    const offsetX = (containerWidth - templateWidth * previewScale) / 2;
    const offsetY = (containerHeight - templateHeight * previewScale) / 2;

    // Room colors by type
    const getRoomColor = (name) => {
      const n = name.toLowerCase();
      if (n.includes('garage') || n.includes('shop')) return 'rgba(100, 100, 120, 0.4)';
      if (n.includes('bedroom') || n.includes('master')) return 'rgba(100, 150, 255, 0.3)';
      if (n.includes('bath')) return 'rgba(100, 200, 255, 0.4)';
      if (n.includes('kitchen')) return 'rgba(255, 200, 100, 0.3)';
      if (n.includes('living') || n.includes('great')) return 'rgba(150, 255, 150, 0.25)';
      if (n.includes('dining')) return 'rgba(255, 180, 100, 0.25)';
      if (n.includes('closet')) return 'rgba(180, 150, 200, 0.3)';
      if (n.includes('laundry') || n.includes('utility')) return 'rgba(200, 200, 200, 0.3)';
      if (n.includes('porch')) return 'rgba(150, 200, 150, 0.2)';
      if (n.includes('office')) return 'rgba(200, 180, 255, 0.3)';
      if (n.includes('pantry')) return 'rgba(220, 180, 140, 0.3)';
      return 'rgba(200, 200, 200, 0.2)';
    };

    // Draw rooms
    selectedTemplate.rooms.forEach(room => {
      const x = offsetX + room.x * scaleFactor * previewScale;
      const y = offsetY + room.y * scaleFactor * previewScale;
      const w = room.width * scaleFactor * previewScale;
      const h = room.height * scaleFactor * previewScale;

      // Fill
      ctx.fillStyle = getRoomColor(room.name);
      ctx.fillRect(x, y, w, h);

      // Border
      ctx.strokeStyle = 'rgba(0, 200, 255, 0.6)';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);

      // Label
      ctx.fillStyle = '#c0d0e0';
      ctx.font = '11px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Truncate long names
      let label = room.name;
      const maxWidth = w - 8;
      while (ctx.measureText(label).width > maxWidth && label.length > 3) {
        label = label.slice(0, -1);
      }
      if (label !== room.name) label += '…';

      if (w > 30 && h > 20) {
        ctx.fillText(label, x + w / 2, y + h / 2);
      }
    });

    // Draw outer border
    ctx.strokeStyle = 'rgba(0, 200, 255, 0.8)';
    ctx.lineWidth = 3;
    ctx.strokeRect(
      offsetX,
      offsetY,
      templateWidth * previewScale,
      templateHeight * previewScale
    );

  }, [selectedTemplate, scaleFactor]);

  const handleUseTemplate = () => {
    if (!selectedTemplate) return;
    onSelect?.({
      template: selectedTemplate,
      scaleFactor,
      scaledDimensions,
    });
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{
        ...styles.modal,
        ...(isMobile ? styles.modalMobile : {}),
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          {isMobile && showPreview ? (
            <Button
              variant="ghost"
              size="small"
              onClick={() => setShowPreview(false)}
              style={{ padding: '4px', marginRight: '8px' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </Button>
          ) : null}
          <h2 style={styles.title}>
            {isMobile && showPreview ? selectedTemplate?.name : 'Floor Plan Templates'}
          </h2>
          <Button variant="ghost" size="small" onClick={onClose} style={{ padding: '4px', marginLeft: 'auto' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Style Filter - only show on list view for mobile */}
        {(!isMobile || !showPreview) && (
          <div style={{
            ...styles.filterRow,
            ...(isMobile ? styles.filterRowMobile : {}),
          }}>
            <Button
              variant={selectedStyle === 'all' ? 'primary' : 'default'}
              size="small"
              onClick={() => setSelectedStyle('all')}
            >
              All
            </Button>
            {templateStyles.map(style => (
              <Button
                key={style}
                variant={selectedStyle === style ? 'primary' : 'default'}
                size="small"
                onClick={() => setSelectedStyle(style)}
                style={{ textTransform: 'capitalize' }}
              >
                {style}
              </Button>
            ))}
          </div>
        )}

        {/* Content */}
        <div style={{
          ...styles.content,
          ...(isMobile ? styles.contentMobile : {}),
        }}>
          {/* Template List - hide on mobile when preview is showing */}
          {(!isMobile || !showPreview) && (
          <div style={{
            ...styles.templateList,
            ...(isMobile ? styles.templateListMobile : {}),
          }}>
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                style={{
                  ...styles.templateCard,
                  ...(selectedTemplate?.id === template.id ? styles.templateCardSelected : {}),
                }}
                onClick={() => {
                  setSelectedTemplate(template);
                  setTargetSqft(template.baseSqft.toString());
                  if (isMobile) setShowPreview(true);
                }}
              >
                <div style={styles.templateName}>{template.name}</div>
                <div style={styles.templateMeta}>
                  <span style={styles.templateStyle}>{template.style}</span>
                  <span>{template.baseSqft.toLocaleString()} sq ft</span>
                </div>
                <div style={styles.templateSpecs}>
                  <span>{template.beds} bed</span>
                  <span style={styles.dot}>•</span>
                  <span>{template.baths} bath</span>
                  <span style={styles.dot}>•</span>
                  <span>{template.totalWidth}' × {template.totalHeight}'</span>
                </div>
                <div style={styles.templateDesc}>{template.description}</div>
              </div>
            ))}
          </div>
          )}

          {/* Preview Panel - show on mobile only when showPreview is true */}
          {(!isMobile || showPreview) && (
          <div style={{
            ...styles.previewPanel,
            ...(isMobile ? styles.previewPanelMobile : {}),
          }}>
            {selectedTemplate ? (
              <>
                {/* Hide header on mobile since it's in the main header */}
                {!isMobile && (
                <div style={styles.previewHeader}>
                  <h3 style={styles.previewTitle}>{selectedTemplate.name}</h3>
                </div>
                )}

                {/* Canvas Preview */}
                <div style={styles.canvasContainer}>
                  <canvas ref={previewCanvasRef} style={styles.canvas} />
                </div>

                {/* Size Adjuster */}
                <div style={styles.sizeAdjuster}>
                  <label style={styles.sizeLabel}>Target Square Footage</label>
                  <div style={styles.sizeInputRow}>
                    <input
                      type="number"
                      value={targetSqft}
                      onChange={e => setTargetSqft(e.target.value)}
                      style={styles.sizeInput}
                      min="500"
                      max="10000"
                      step="100"
                    />
                    <span style={styles.sizeUnit}>sq ft</span>
                  </div>
                  <input
                    type="range"
                    min={Math.round(selectedTemplate.baseSqft * 0.5)}
                    max={Math.round(selectedTemplate.baseSqft * 2)}
                    step="50"
                    value={targetSqft || selectedTemplate.baseSqft}
                    onChange={e => setTargetSqft(e.target.value)}
                    style={styles.sizeSlider}
                  />
                  <div style={styles.sizeRange}>
                    <span>{Math.round(selectedTemplate.baseSqft * 0.5).toLocaleString()}</span>
                    <span>{Math.round(selectedTemplate.baseSqft * 2).toLocaleString()}</span>
                  </div>
                </div>

                {/* Scaled Info */}
                {scaledDimensions && (
                  <div style={styles.scaledInfo}>
                    <div style={styles.scaledRow}>
                      <span style={styles.scaledLabel}>Scaled Size:</span>
                      <span style={styles.scaledValue}>
                        {scaledDimensions.width}' × {scaledDimensions.height}'
                      </span>
                    </div>
                    <div style={styles.scaledRow}>
                      <span style={styles.scaledLabel}>Total Area:</span>
                      <span style={styles.scaledValue}>
                        {scaledDimensions.sqft.toLocaleString()} sq ft
                      </span>
                    </div>
                    <div style={styles.scaledRow}>
                      <span style={styles.scaledLabel}>Scale Factor:</span>
                      <span style={styles.scaledValue}>
                        {(scaleFactor * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Room Count */}
                <div style={styles.roomCount}>
                  {selectedTemplate.rooms.length} rooms in template
                </div>

                {/* Use Button */}
                <Button
                  variant="primary"
                  onClick={handleUseTemplate}
                  style={styles.useButton}
                >
                  Use This Template
                </Button>
              </>
            ) : (
              <div style={styles.noSelection}>
                <div style={styles.noSelectionIcon}>📐</div>
                <div style={styles.noSelectionText}>
                  Select a template to preview
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: 'linear-gradient(180deg, #1a2030 0%, #151a25 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  title: {
    margin: 0,
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    overflowX: 'auto',
  },
  content: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  templateList: {
    width: '320px',
    minWidth: '320px',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    overflowY: 'auto',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  templateCard: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '8px',
    padding: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  templateCardSelected: {
    background: 'rgba(0, 200, 255, 0.1)',
    borderColor: 'rgba(0, 200, 255, 0.4)',
  },
  templateName: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  templateMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#8090a0',
    marginBottom: '4px',
  },
  templateStyle: {
    background: 'rgba(0, 200, 255, 0.15)',
    color: '#00c8ff',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    textTransform: 'capitalize',
  },
  templateSpecs: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: '#6080a0',
    marginBottom: '6px',
  },
  dot: {
    opacity: 0.5,
  },
  templateDesc: {
    fontSize: '11px',
    color: '#607080',
    lineHeight: 1.4,
  },
  previewPanel: {
    flex: 1,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  previewHeader: {
    marginBottom: '12px',
  },
  previewTitle: {
    margin: 0,
    color: '#00c8ff',
    fontSize: '16px',
    fontWeight: '600',
  },
  canvasContainer: {
    flex: 1,
    minHeight: '200px',
    maxHeight: '300px',
    background: '#1a2030',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    overflow: 'hidden',
    padding: '16px',
  },
  canvas: {
    display: 'block',
  },
  sizeAdjuster: {
    marginTop: '16px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '8px',
  },
  sizeLabel: {
    display: 'block',
    color: '#8090a0',
    fontSize: '12px',
    marginBottom: '8px',
  },
  sizeInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  sizeInput: {
    width: '120px',
    padding: '8px 12px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
  },
  sizeUnit: {
    color: '#6080a0',
    fontSize: '14px',
  },
  sizeSlider: {
    width: '100%',
    height: '6px',
    borderRadius: '3px',
    background: 'rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
  },
  sizeRange: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '10px',
    color: '#506070',
    marginTop: '4px',
  },
  scaledInfo: {
    marginTop: '12px',
    padding: '12px',
    background: 'rgba(0, 200, 255, 0.05)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 200, 255, 0.15)',
  },
  scaledRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  scaledLabel: {
    color: '#8090a0',
    fontSize: '12px',
  },
  scaledValue: {
    color: '#00c8ff',
    fontSize: '12px',
    fontWeight: '600',
  },
  roomCount: {
    marginTop: '8px',
    textAlign: 'center',
    color: '#506070',
    fontSize: '11px',
  },
  useButton: {
    marginTop: '16px',
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
  },
  noSelection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#506070',
  },
  noSelectionIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  noSelectionText: {
    fontSize: '14px',
  },

  // Mobile overrides
  modalMobile: {
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    borderRadius: 0,
    margin: 0,
  },
  filterRowMobile: {
    padding: '8px 12px',
    gap: '6px',
  },
  contentMobile: {
    flexDirection: 'column',
  },
  templateListMobile: {
    width: '100%',
    minWidth: 'unset',
    borderRight: 'none',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    maxHeight: 'none',
    flex: 1,
  },
  previewPanelMobile: {
    flex: 1,
    padding: '12px',
    overflow: 'auto',
  },
};

export default TemplateSelector;
